import { AudioContent } from 'components/content/AudioContent';
import { DocumentContent } from 'components/content/DocumentContent';
import { HyperTextContent } from 'components/content/HyperTextContent';
import { IframeContent } from 'components/content/IframeContent';
import { LtiContent } from 'components/content/LtiContent';
import { Modal, ModalProps, Space, Spin } from 'antd';
import { VideoContent } from 'components/content/VideoContent';
import { getAPI } from 'requests/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { IDataContent } from 'interfaces/courses';

interface ContentPreviewModalProps {
  modalProps: ModalProps;
  previewUrl?: string;
  node?: IDataContent;
}

const contentTypeMap: Record<IDataContent['content_type'], React.ElementType> =
  {
    hyper_doc: HyperTextContent,
    html_doc: HyperTextContent,
    hyper_text: HyperTextContent,
    audio: AudioContent,
    video: VideoContent,
    document: DocumentContent,
    lti: LtiContent
  };

export const ContentPreviewModal = ({
  modalProps,
  previewUrl,
  node
}: ContentPreviewModalProps) => {
  const { id } = useParams<{ id: string }>();

  const [content, setContent] = useState<IDataContent | null>(null);
  const [loading, setLoading] = useState(false);

  const ContentComponent = useMemo(() => {
    return content?.content_type ? contentTypeMap[content.content_type] : null;
  }, [content]);

  const getPreviewSrc = (data: IDataContent): string => {
    if (data.core_kind === 'lti' && data.core_url) {
      return data.core_url;
    }
    return data.preview_url || data.url || '';
  };

  const fetchContent = useCallback(async () => {
    if (!node || !id || previewUrl) {
      return;
    }

    setLoading(true);

    try {
      const { data } = await getAPI().get(
        `admin/professor_area/classrooms/${id}/contents/${node.key}`
      );
      setContent(data);
    } finally {
      setLoading(false);
    }
  }, [node, id, previewUrl]);

  useEffect(() => {
    if (node) {
      fetchContent();
    }
  }, [node, fetchContent]);

  const ContentModal = () => {
    const renderCentered = (children: React.ReactNode) => (
      <div className="w-full h-[200px] flex items-center justify-center">
        {children}
      </div>
    );

    if (loading) {
      return renderCentered(<Spin tip="Carregando conteúdo..." size="large" />);
    }

    if (!previewUrl && !content) {
      return renderCentered(<span>Nenhum conteúdo disponível.</span>);
    }

    if (previewUrl) {
      return <IframeContent src={previewUrl} title="Preview" />;
    }

    if (content && ContentComponent) {
      return (
        <ContentComponent src={getPreviewSrc(content)} title={content.name} />
      );
    }

    return null;
  };

  return (
    <Modal
      {...modalProps}
      className="!w-[60vw]"
      title={node?.title?.toString() || 'Material complementar'}
      footer={null}
    >
      <Space
        direction="vertical"
        className="!w-full flex items-center"
        styles={{ item: { width: '100%' } }}
      >
        <ContentModal />
      </Space>
    </Modal>
  );
};
